import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import CargomaticInput from 'components/v2/input';
import { decrypt, encrypt } from 'utils/Encryption';
import { carrierEdiEvents, ediEvents } from '../shipments/mappings';
import Checkbox from '../../v2/forms/Checkbox';
import CargomaticSelect from '../../v2/select';
import ErrorAlert from '../../ui/ErrorAlert';
import { CUSTOMER_TYPE, webhookTypes } from '../../../constants/webhooks';
import SessionStore from '../../../stores/SessionStore';
import AddButton from '../../v2/buttons/AddButton';
import DeleteButton from '../../v2/buttons/DeleteButton';

function mapPropsToState(props) {
  const {
    webhook: { authentication },
  } = props;

  const state = {
    url: '',
    enabled: true,
    events: [],
    type: CUSTOMER_TYPE.value,
    ...props.webhook,
    authentication: {
      enabled: authentication ? authentication.enabled : false,
      clientId:
        authentication && authentication.clientId
          ? decrypt(authentication.clientId)
          : null,
      clientSecret:
        authentication && authentication.clientSecret
          ? decrypt(authentication.clientSecret)
          : null,
    },
  };

  if (!state.headers || !state.headers.length) {
    state.headers = [{ key: '', value: '' }];
  }

  return state;
}

function hasDuplicatesKey(headers) {
  const keySet = new Set(Object.keys(headers));

  return keySet.size !== headers.length;
}

function cleanHeaders(headers) {
  return headers.filter((header) => header.key);
}

class Form extends Component {
  static propTypes = {
    accountType: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      webhook: mapPropsToState(props),
      errors: {},
    };
  }

  handleSelectType = (item) => {
    const { value: type } = item;
    this.setState((prevState) => ({
      webhook: {
        ...prevState.webhook,
        type,
      },
    }));
  };

  handleCancel = () => this.props.onCancel();

  handleSubmit = (e) => {
    e.preventDefault();

    const {
      webhook: { url, events, type, authentication },
    } = this.state;
    const headers = cleanHeaders(this.state.webhook.headers);
    const { t } = this.props;
    const errors = {};

    if (!type) {
      errors.type = 'Please select the type.';
    }

    if (!url) {
      errors.url = t('URL_FIELD_IS_REQUIRED');
    } else if (!this.urlRef.checkValidity()) {
      errors.url = t('ENTER_VALID_URL');
    }

    if (!events || events.length === 0) {
      errors.events = t('SELECT_ATLEAST_ONE_EVENT');
    }

    if (hasDuplicatesKey(headers)) {
      errors.headers = t('HEADER_KEYS_MUST_BE_UNIQUE');
    }

    if (
      authentication.enabled &&
      (!authentication.clientId || !authentication.clientSecret)
    ) {
      errors.authentication = t('NO_AUTH_DATA');
    }

    this.setState({ errors });

    if (Object.keys(errors).length === 0) {
      this.props.onSave({
        ...this.state.webhook,
        headers,
        authentication: {
          enabled: authentication.enabled,
          clientId: authentication.clientId && encrypt(authentication.clientId),
          clientSecret:
            authentication.clientSecret && encrypt(authentication.clientSecret),
        },
      });
    }
  };

  handleUrlChange = (e) => {
    const {
      target: { value: url },
    } = e;

    this.handleFieldChange('url', url);
  };

  handleEnabledChange = (enabled) => {
    this.handleFieldChange('enabled', enabled);
  };

  handleAuthChange = (enabled) => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      newState.webhook = {
        ...prevState.webhook,
        authentication: {
          ...prevState.webhook.authentication,
          enabled,
        },
      };
      return newState;
    });
  };

  handleAuthDataChange = (field, value) => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      newState.webhook = {
        ...prevState.webhook,
        authentication: {
          ...prevState.webhook.authentication,
          [field]: value,
        },
      };
      return newState;
    });
  };

  handleEventChange = (value) => (checked) => {
    this.setState((prevState) => {
      const { webhook: currentWebhook } = prevState;
      const { events: selectedEvents } = currentWebhook;
      const eventsList =
        this.props.accountType === 'Carrier' ? carrierEdiEvents : ediEvents;

      let events;
      if (!checked) {
        events = selectedEvents
          .filter((event) => event.slug !== value)
          .map((event) => ({ slug: event.slug }));
      } else {
        events = [
          ...selectedEvents,
          ...eventsList
            .filter((event) => event.slug === value)
            .map((event) => ({ slug: event.slug })),
        ];
      }
      return {
        webhook: {
          ...currentWebhook,
          events,
        },
      };
    });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => {
      const { webhook: currentWebhook } = prevState;

      return {
        webhook: {
          ...currentWebhook,
          [field]: value,
        },
      };
    });
  };

  handleAddHeader = () => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      newState.webhook.headers.push({ key: '', value: '' });

      return newState;
    });
  };

  handleChangeHeaderKey = (index, newKey) => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      const header = newState.webhook.headers[index];

      header.key = newKey;

      return newState;
    });
  };

  handleChangeHeaderValue = (index, value) => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      const header = newState.webhook.headers[index];

      header.value = value;

      return newState;
    });
  };

  handleDeleteHeader = (key) => {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
      };

      const index = newState.webhook.headers.findIndex(
        (header) => header.key === key,
      );

      newState.webhook.headers.splice(index, 1);

      if (!prevState.webhook.headers.length) {
        newState.webhook.headers.push({ key: '', value: '' });
      }

      return newState;
    });
  };

  renderErrors() {
    const { errors } = this.state;
    const errorKeys = Object.keys(errors);
    const { t } = this.props;
    if (errorKeys.length === 0) {
      return null;
    }

    return (
      <ErrorAlert title={t('PLEASE_FIX_ERRORS')}>
        <ul className="list-reset">
          {errorKeys.map((errorKey) => (
            <li key={errorKey}>{errors[errorKey]}</li>
          ))}
        </ul>
      </ErrorAlert>
    );
  }

  render() {
    const {
      webhook: { url, enabled, events, authentication },
    } = this.state;
    const { t } = this.props;
    const selected = events.map((e) => e.slug);
    const eventsList =
      this.props.accountType === 'Carrier' ? carrierEdiEvents : ediEvents;

    return (
      <form noValidate onSubmit={this.handleSubmit}>
        {this.renderErrors()}
        <div className="flex -mx-2">
          <div className="flex-1 px-2 mb-4">
            <label className="block mb-2 form-label">{t('URL')}</label>
            <input
              type="url"
              value={url}
              className="w-full form-field"
              placeholder="Enter the URL"
              ref={(input) => {
                this.urlRef = input;
              }}
              onChange={this.handleUrlChange}
              required
            />
          </div>
        </div>
        <div className="-mx-2">
          <div className="px-2 mb-4">
            <label className="block mb-2 form-label">{t('HEADERS')}</label>
            <div className="flex flex-col gap-2">
              {this.state.webhook.headers.map((header, i) => (
                <div className="flex gap-4" key={i}>
                  <CargomaticInput
                    className="flex-1"
                    type="text"
                    value={header.key}
                    placeholder={t('PLEASE_ENTER_HEADER_KEY')}
                    onChange={(value) => {
                      this.handleChangeHeaderKey(i, value);
                    }}
                    autoComplete="one-time-code"
                  />
                  <CargomaticInput
                    className="flex-1"
                    type="password"
                    value={header.value}
                    placeholder={t('PLEASE_ENTER_HEADER_VALUE')}
                    onChange={(value) => {
                      this.handleChangeHeaderValue(i, value);
                    }}
                    autoComplete="one-time-code"
                  />
                  <DeleteButton
                    className="mt-2 flex-none"
                    onClick={() => {
                      this.handleDeleteHeader(header.key);
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-4">
              <AddButton
                onClick={() => {
                  this.handleAddHeader();
                }}
              />
            </div>
          </div>
        </div>

        <div className="mb-8">
          <label className="block form-label">
            {`${t('REQUIRES_AUTHENTICATION')}`}?
          </label>
          <div>
            <label className="py-2">
              <Switch
                className="mb-0"
                checked={authentication.enabled}
                onChange={this.handleAuthChange}
              />
            </label>
          </div>
          {authentication.enabled && (
            <div className="grid gap-4 grid-cols-2">
              <CargomaticInput
                type="text"
                placeholder={t('PLEASE_ENTER_CLIENT_ID')}
                onChange={(val) => this.handleAuthDataChange('clientId', val)}
                value={authentication.clientId}
              />
              <CargomaticInput
                type="password"
                placeholder={t('PLEASE_ENTER_CLIENT_SECRET')}
                onChange={(val) =>
                  this.handleAuthDataChange('clientSecret', val)
                }
                value={authentication.clientSecret}
              />
            </div>
          )}
        </div>

        <div className="grid gap-4 grid-cols-2">
          <div className="">
            <label className="block form-label">{`${t('ENABLED')}`}?</label>
            <div className="mb-4">
              <label className="py-2">
                <Switch
                  className="mb-0"
                  checked={enabled}
                  onChange={this.handleEnabledChange}
                />
              </label>
            </div>
          </div>

          {SessionStore.isAdmin() && (
            <CargomaticSelect
              label={t('TYPE')}
              info={t('INTERNAL_VS_CUSTOMER')}
              caption={t('SELECT_THE_TYPE')}
              items={webhookTypes}
              valueKey="value"
              textKey="localizationSlug"
              value={this.state.webhook.type}
              onSelect={this.handleSelectType}
            />
          )}
        </div>
        <div className="mb-4">
          <label className="block mb-1 form-label">
            {t('EVENT', { count: 2 })}
          </label>
          <div className="grid grid-cols-2 gap-4">
            {eventsList.map((ediEvent) => (
              <Checkbox
                label={t(ediEvent.localizationSlug)}
                id={ediEvent.slug}
                name={ediEvent.slug}
                value={ediEvent.slug}
                checked={selected.includes(ediEvent.slug)}
                onChange={this.handleEventChange(ediEvent.slug)}
              >
                {t(ediEvent.localizationSlug)}
              </Checkbox>
            ))}
          </div>
          <div className="flex justify-end">
            <button type="submit" className="button button-green">
              {titleize(t('SAVE'))}
            </button>
            <button
              onClick={this.handleCancel}
              className="button button-secondary"
            >
              {t('CANCEL')}
            </button>
          </div>
        </div>
      </form>
    );
  }
}

export default withTranslation()(Form);
